// Shared account / licensing types. No DOM, no Node-only APIs:
// this package is imported by the web app, the server and their tests.

export type SubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'expired';

export type PlanEntitlements = {
  /** whether image export is allowed at all */
  export: boolean;
  /** whether exports should be watermarked */
  watermark: boolean;
  /** maximum concurrently usable devices */
  maxDevices: number;
};

export type Plan = {
  id: string;
  name: string;
  /** placeholder price in the account currency */
  priceMonthly: number | null;
  /** placeholder yearly price (null when the plan has no billing cycle) */
  priceYearly: number | null;
  /** shown as the recommended plan on the pricing page */
  recommended?: boolean;
  entitlements: PlanEntitlements;
  /** short marketing line, i18n keys resolved on the client */
  blurb: string;
};

/**
 * Claims of the signed license token.
 * `gu` (grace-until) allows the client to keep working offline past `exp`.
 */
export type LicenseClaims = {
  sub: string; // user id
  did: string; // device id
  plan: string;
  status: SubscriptionStatus;
  entitlements: {
    export: boolean;
    watermark: boolean;
  };
  iat: number;
  exp: number; // 24 h after iat
  gu: number; // grace-until = exp + GRACE_DAYS
  /** trial-ends-at (epoch s) — present only for `trialing` tokens; the
   *  short-lived token exp is NOT the trial window. */
  tee?: number;
};

/**
 * A claim is usable if it is signed, not expired, and still inside grace.
 * Export is allowed only before `exp`; app view stays until `gu`.
 */
export function licenseUsable(claims: LicenseClaims, now: number): boolean {
  return now < claims.exp;
}

/** App stays usable (view mode) until the grace deadline. */
export function licenseInGrace(claims: LicenseClaims, now: number): boolean {
  return now >= claims.exp && now < claims.gu;
}

/**
 * Clock-tamper check (#8): the token must never be valid when the device
 * clock runs EARLIER than the last time the server was contacted (token iat).
 */
export function licenseClockSane(claims: LicenseClaims, lastSeenServerTime: number): boolean {
  if (!Number.isFinite(lastSeenServerTime)) return true;
  return claims.iat >= lastSeenServerTime;
}
